import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import { AddOnTextInput, Button, Loading, Panel, Typography, Modal, Skeleton } from "@/components/atoms";
import { ConfigDetail } from "@/components/molecules";
import { PageHeader } from "@/components/organisms";
import { getDirtyValues } from "@/helpers/formHelpers";
import { usePrompt } from "@/hooks/usePrompt";
import { useGetWaitingTimesQuery, useUpdateWaitingTimesMutation } from "@/redux/apis/config/admin/waitingTimes";
import { useAppSelector } from "@/redux/hooks";
import { waitingTimesSelector } from "@/redux/slices/admin/selectors";
import { WaitingTimes } from "@/redux/slices/admin/types";
import { addToast } from "@/utils/addToast";

const modals = {
  exit: {
    title: "Discard Changes",
    description: "Changes will not be saved. Do you want to proceed?",
  },
  error: {
    title: "Unable to Save Waiting Times",
    description: "There is an error in one of the fields submitted. Please double check your inputs and try again.",
  },
};

type FormData = WaitingTimes;

export const WaitingTimesEdit = () => {
  const { isFetching } = useGetWaitingTimesQuery();
  const { details } = useAppSelector(waitingTimesSelector);
  const [updateWaitingTimes, { isLoading }] = useUpdateWaitingTimesMutation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty, dirtyFields, isSubmitted },
  } = useForm<FormData>({ defaultValues: details });
  const [activeModal, setActiveModal] = useState<"exit" | "error" | null>(null);
  const navigate = useNavigate();
  const { ref, entry, inView } = useInView({
    rootMargin: "-180px 0px 0px 0px",
  });

  useEffect(() => {
    reset(details);
  }, [details, reset]);

  const promptWhen = isDirty && activeModal !== "exit" && !isSubmitted;
  usePrompt({ when: promptWhen, message: "Changes will not be saved. Do you want to proceed?" });

  const onSubmit = handleSubmit((data) => {
    const dirtyValues = getDirtyValues(dirtyFields, data) as WaitingTimes;
    const { internationalAirport, domesticAirport, standard } = { ...dirtyValues };

    setActiveModal(null);
    updateWaitingTimes({
      international_airport: internationalAirport,
      domestic_airport: domesticAirport,
      standard,
    })
      .unwrap()
      .then(() => {
        addToast("success", `Successfully Updated Waiting Times`);
        navigate("../");
      })
      .catch(() => setActiveModal("error"));
  });

  const renderModal = () => {
    if (!activeModal) return;

    return (
      <Modal
        open={true}
        title={modals[activeModal].title}
        description={modals[activeModal].description}
        onClose={() => setActiveModal(null)}
        renderButtons={renderModalButtons}
      />
    );
  };

  const renderModalButtons = () => {
    switch (activeModal) {
      case "exit":
        return (
          <>
            <Button variant="secondary" onClick={() => setActiveModal(null)}>
              Cancel
            </Button>
            <Button onClick={() => navigate("../")}>Discard Changes</Button>
          </>
        );
      default:
        return (
          <Button onClick={() => setActiveModal(null)} className="px-8">
            Back
          </Button>
        );
    }
  };

  if (isFetching) return <Skeleton />;

  return (
    <form onSubmit={onSubmit}>
      <PageHeader.Actions>
        {entry && !inView && (
          <div className="ml-auto flex animate-enter gap-3">
            <Button variant="secondary" onClick={() => (isDirty ? setActiveModal("exit") : navigate("../"))} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isDirty || isLoading}>
              Save
            </Button>
          </div>
        )}
      </PageHeader.Actions>
      <Panel>
        <div className="relative">
          {renderModal()}
          {isLoading && <Loading />}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Typography variant="h3" className="leading-8">
                Waiting Times
              </Typography>
              <Typography className="text-neutral-dark-gray">
                Set free waiting time periods to all drivers and customers for all your own jobs.
              </Typography>
            </div>
            <div className="flex gap-3" ref={ref}>
              <Button variant="secondary" onClick={() => (isDirty ? setActiveModal("exit") : navigate("../"))}>
                Cancel
              </Button>
              <Button type="submit" disabled={!isDirty}>
                Save
              </Button>
            </div>
          </div>
          <ConfigDetail title="International Airport" description="Free (default) waiting times for an international airport pickup.">
            <AddOnTextInput
              className="w-[150px]"
              endAddOn="Minutes"
              type="number"
              step="1"
              min="0"
              max="999"
              {...register("internationalAirport")}
            />
          </ConfigDetail>
          <ConfigDetail title="Domestic Airport" description="Free (default) waiting times for a domestic airport pickup.">
            <AddOnTextInput
              className="w-[150px]"
              endAddOn="Minutes"
              type="number"
              step="1"
              min="0"
              max="999"
              {...register("domesticAirport")}
            />
          </ConfigDetail>
          <ConfigDetail title="All other trips" description="Free (default) waiting times for all other trip.">
            <AddOnTextInput className="w-[150px]" endAddOn="Minutes" type="number" step="1" min="0" max="999" {...register("standard")} />
          </ConfigDetail>
        </div>
      </Panel>
    </form>
  );
};
